// PAPERLIKE CUSTOM — Paperlike-built, no vanilla equivalent
// Inline (non-popup) fork of the PDP variant-selector-modal element (variant-selector-modal.js).
// Renders the two-step iPad picker directly inside a section, driven by blocks/variant-picker.liquid.
// Every DOM query is scoped to the element, so this can never interfere with the PDP modal.
// Strings arrive via data attributes on the element (the modal reads themeVariables.strings.*,
// which are not defined anywhere in the theme — that quirk is avoided here).
// The "Can't find your iPad?" search popup is NOT handled here — the variant-model-search
// element from variant-selector-modal.js (loaded alongside) drives it unchanged.

if (!window.customElements.get('variant-selector-inline')) {
  // Ported from variant-selector-modal.js — vanilla theme.js does not expose formatMoney.
  function formatMoney(cents, format = "") {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
    function defaultTo(value, defaultValue) {
      return value == null || value !== value ? defaultValue : value;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ",");
      decimal = defaultTo(decimal, ".");
      if (isNaN(number) || number == null) {
        return 0;
      }
      number = (number / 100).toFixed(precision);
      let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
      return dollarsAmount + centsAmount;
    }
    let value = "";
    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
      case "amount_no_decimals_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 0, "'");
        break;
    }
    return formatString.replace(placeholderRegex, value);
  }

  class VariantSelectorInline extends HTMLElement {
    selectors = {
      addToCartButton: '[type="submit"]',
      compareAtPrice: '.pl-variant-picker__footer compare-at-price',
      emptyError: '.variant-selector__empty',
      familyCards: '[data-family-item]',
      familyCardsWrapper: '[data-family-items]',
      familyStep: '[data-family-selection]',
      formWrapper: '[data-form-wrapper]',
      fromElement: '.popup-modal__footer--pricing-label--from',
      modelCards: '[data-model-item]',
      modelCardsWrapper: '[data-model-items]',
      modelStep: '[data-model-selection]',
      previousButton: '[data-modal-previous]',
      salePrice: '.pl-variant-picker__footer sale-price',
      totalElement: '.popup-modal__footer--pricing-label--total',
      variantIdInput: 'input[name="id"]',
      variantList: '.variant-selector__list'
    }

    classes = {
      active: 'active',
      complete: 'complete',
      hidden: 'hidden',
      largeText: 'text-lg',
      saleText: 'text-on-sale',
      selected: 'selected',
      show: 'show',
      subdued: 'subdued',
      subduedBtn: 'button--subdued'
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.previousButton = this.querySelector(this.selectors.previousButton);
      this.familyStepEl = this.querySelector(this.selectors.familyStep);
      this.modelStepEl = this.querySelector(this.selectors.modelStep);
      this.familyCardsWrapper = this.querySelector(this.selectors.familyCardsWrapper);
      this.modelCardsWrapper = this.querySelector(this.selectors.modelCardsWrapper);
      this.familyList = this.familyCardsWrapper?.querySelector(this.selectors.variantList);
      this.modelList = this.modelCardsWrapper?.querySelector(this.selectors.variantList);
      this.familyCards = Array.from(this.querySelectorAll(this.selectors.familyCards));
      this.modelCards = Array.from(this.querySelectorAll(this.selectors.modelCards));
      this.emptyError = this.querySelector(this.selectors.emptyError);

      this.formWrapper = this.querySelector(this.selectors.formWrapper);
      this.addToCartButton = this.formWrapper?.querySelector(this.selectors.addToCartButton);
      this.variantInput = this.formWrapper?.querySelector(this.selectors.variantIdInput);

      // Snapshot the pristine footer state so resetPicker() can restore it after an add
      this.initialVariantId = this.variantInput?.value;
      const initialPriceEl = this.querySelector(this.selectors.salePrice);
      const initialCompareEl = this.querySelector(this.selectors.compareAtPrice);
      this.initialPriceState = {
        priceHtml: initialPriceEl?.innerHTML,
        priceOnSale: initialPriceEl?.classList.contains(this.classes.saleText),
        compareHtml: initialCompareEl?.innerHTML,
        compareHidden: initialCompareEl ? initialCompareEl.hidden : true
      };

      this.setAtcEnabled(false);

      this.familyCards.forEach((card) => {
        card.addEventListener('click', () => this.handleFamilySelection(card));
      });
      this.modelCards.forEach((card) => {
        // OOS models render greyed-out and stay inert (no BIS/upgrade in the inline picker)
        if (card.dataset.variantAvailable !== 'true') return;
        card.addEventListener('click', () => this.handleModelSelection(card));
      });
      this.previousButton?.addEventListener('click', () => this.handlePrevious());

      const keyActivate = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.currentTarget.click();
        }
      };
      [...this.familyCards, ...this.modelCards, this.previousButton].forEach((el) => {
        el?.addEventListener('keydown', keyActivate);
      });

      this.initDeliveryEstimate();

      // No dark overlay dim inline (unlike the PDP) — but the search panel's transparent
      // overlay still spans the whole picker while open, so a click anywhere on the picker
      // outside the panel closes it.
      const searchModal = this.querySelector('variant-model-search');
      const searchOverlay = searchModal?.querySelector('.popup-modal__overlay--search');
      searchOverlay?.addEventListener('click', (event) => {
        if (!event.target.closest('.popup-modal__outer')) searchModal.close?.();
      });

      // Results dropdown height guard — its CSS max-height (min(60dvh, 550px)) is viewport-
      // relative, but the panel can be much shorter when the picker itself is small; cap it
      // to the panel's actual free space above the input area.
      const searchOuter = searchModal?.querySelector('.popup-modal__outer');
      const searchWrapper = searchModal?.querySelector('.variant-selector__search--wrapper');
      const searchResults = searchModal?.querySelector('.variant-selector__search--results');
      const searchInput = searchModal?.querySelector('#ModelSearch');
      const capSearchResults = () => {
        if (!searchOuter || !searchWrapper || !searchResults) return;
        const available = searchOuter.clientHeight - searchWrapper.offsetHeight - 24;
        if (available > 0) searchResults.style.maxHeight = Math.min(available, 550) + 'px';
      };
      searchInput?.addEventListener('input', capSearchResults);
      window.addEventListener('resize', capSearchResults);

      // After a successful add (main ATC or a search-row Add — the theme's product-form fires
      // variant:add for both, and the search sheet already closes itself on it), the cart
      // drawer opens over the page; reset the picker behind it back to a pristine step 1.
      document.addEventListener('variant:add', () => this.resetPicker());
    }

    resetPicker() {
      this.familyCards.forEach((card) => {
        card.classList.remove(this.classes.selected);
        card.classList.remove(this.classes.subdued);
      });
      this.modelCards.forEach((card) => {
        card.classList.remove(this.classes.selected);
        card.classList.remove(this.classes.subdued);
      });
      this.selectedFamilyCard = null;

      this.setStep(this.familyStepEl, this.dataset.textSelectFamily, false, true);
      this.setStep(this.modelStepEl, this.dataset.textSelectModel, false, false);

      this.modelList?.classList.remove(this.classes.active);
      this.familyList?.classList.add(this.classes.active);

      if (this.previousButton) {
        this.previousButton.classList.remove(this.classes.show);
        this.previousButton.removeAttribute('tabindex');
      }

      if (this.variantInput && this.initialVariantId) this.variantInput.value = this.initialVariantId;
      this.setAtcEnabled(false);
      this.toggleFromElement(true);

      const priceElement = this.querySelector(this.selectors.salePrice);
      if (priceElement && this.initialPriceState.priceHtml != null) {
        priceElement.innerHTML = this.initialPriceState.priceHtml;
        priceElement.classList.toggle(this.classes.saleText, this.initialPriceState.priceOnSale);
      }
      const compareAtPriceElement = this.querySelector(this.selectors.compareAtPrice);
      if (compareAtPriceElement && this.initialPriceState.compareHtml != null) {
        compareAtPriceElement.innerHTML = this.initialPriceState.compareHtml;
        compareAtPriceElement.hidden = this.initialPriceState.compareHidden;
      }

      if (this.shippingEstEl) {
        this.currentPriceCents = this.initialPriceCents ?? this.currentPriceCents;
        this.renderDeliveryEstimate();
      }
    }

    // Lean port of the PDP delivery-promise script (product-info.liquid ~L1543): same
    // shipping_config metafield + currency-aware free-shipping threshold + cart-total
    // awareness. No backorder-label handling (that mapping is a PDP section setting).
    initDeliveryEstimate() {
      this.shippingEstEl = this.querySelector('[data-shipping-est]');
      const dataEl = this.querySelector('[data-shipping-data]');
      if (!this.shippingEstEl || !dataEl) return;

      let data;
      try {
        data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      this.shippingStrings = data.strings || {};
      this.currentPriceCents = Number(data.initialPrice) || 0;
      this.initialPriceCents = this.currentPriceCents;
      this.cachedCartTotal = 0;

      const getCookie = (name) => {
        const eq = name + '=';
        for (let c of document.cookie.split(';')) {
          c = c.trim();
          if (c.startsWith(eq)) return c.slice(eq.length);
        }
        return null;
      };

      const countryCode = getCookie('localization');
      const countryData = (data.config || {})[countryCode] || {};
      this.shippingTime = countryData.shipping_time ?? null;

      // Threshold is denominated in countryData.currency (missing key = EUR, old shape);
      // prices below are in presentment currency, so convert before comparing.
      const thresholdMajor = countryData.free_threshold ?? null;
      const thresholdCurrency = countryData.currency ?? 'EUR';
      this.thresholdCents = null;
      if (thresholdMajor != null) {
        const activeCurrency = (window.Shopify?.currency?.active) || 'EUR';
        const rate = parseFloat(window.Shopify?.currency?.rate) || 1;
        if (thresholdCurrency === activeCurrency) {
          this.thresholdCents = Math.round(thresholdMajor * 100);
        } else if (thresholdCurrency === 'EUR') {
          this.thresholdCents = Math.round(thresholdMajor * 100 * rate);
        }
      }

      this.renderDeliveryEstimate();

      const refreshCartTotal = async () => {
        try {
          const res = await fetch('/cart.js');
          const cart = await res.json();
          this.cachedCartTotal = cart.total_price || 0;
        } catch (e) {
          // network error — keep last known total
        }
        this.renderDeliveryEstimate();
      };
      refreshCartTotal();
      document.addEventListener('variant:add', refreshCartTotal);
      ['cart:updated', 'cart:change', 'theme:cart-updated'].forEach((evt) =>
        document.addEventListener(evt, refreshCartTotal)
      );
    }

    renderDeliveryEstimate() {
      if (!this.shippingEstEl) return;
      const s = this.shippingStrings;
      if (!this.shippingTime) {
        this.shippingEstEl.textContent = s.unknownLocation || '';
        return;
      }
      const isFree = this.thresholdCents !== null && (
        this.currentPriceCents >= this.thresholdCents || this.cachedCartTotal >= this.thresholdCents
      );
      this.shippingEstEl.textContent = isFree
        ? `${s.freeDelivery} ${this.shippingTime} ${s.days}.`
        : `${s.deliveryIn} ${this.shippingTime} ${s.days}.`;
    }

    handleFamilySelection(family) {
      this.selectedFamilyCard = family;
      this.familyCards.forEach((card) => {
        card.classList.toggle(this.classes.selected, card === family);
        card.classList.toggle(this.classes.subdued, card !== family);
      });

      const selectedFamily = family.dataset.family;
      this.visibleModels = this.modelCards.filter((model) => model.dataset.family === selectedFamily);
      this.modelCards.forEach((model) => {
        model.classList.toggle(this.classes.hidden, !this.visibleModels.includes(model));
      });
      this.emptyError?.classList.toggle(this.classes.show, this.visibleModels.length === 0);

      this.setStep(this.familyStepEl, selectedFamily, true, true);
      this.setStep(this.modelStepEl, null, false, true);
      this.showModels();
    }

    showModels() {
      this.familyList?.classList.remove(this.classes.active);
      this.modelList?.classList.add(this.classes.active);

      if (this.previousButton) {
        this.previousButton.classList.add(this.classes.show);
        this.previousButton.setAttribute('tabindex', '0');
      }

      const firstVisible =
        this.modelCards.find((model) => model.classList.contains(this.classes.selected)) ||
        this.modelCards.find((model) => !model.classList.contains(this.classes.hidden));
      if (firstVisible) {
        firstVisible.focus();
      } else {
        this.emptyError?.focus();
      }
    }

    handlePrevious() {
      this.setStep(this.familyStepEl, null, false, true);
      this.setStep(this.modelStepEl, this.dataset.textSelectModel, false, false);

      this.modelCards.forEach((card) => {
        card.classList.remove(this.classes.selected);
        card.classList.remove(this.classes.subdued);
      });

      this.modelList?.classList.remove(this.classes.active);
      this.familyList?.classList.add(this.classes.active);

      if (this.previousButton) {
        this.previousButton.classList.remove(this.classes.show);
        this.previousButton.removeAttribute('tabindex');
      }

      this.setAtcEnabled(false);
      this.toggleFromElement(true);
      this.selectedFamilyCard?.focus();
    }

    handleModelSelection(model) {
      this.modelCards.forEach((card) => {
        card.classList.toggle(this.classes.selected, card === model);
        card.classList.toggle(this.classes.subdued, card !== model);
      });

      if (this.variantInput) this.variantInput.value = model.dataset.variantId;

      try {
        // Read by PDP/cart scripts — same key the modal writes
        localStorage.setItem('selectedIpadSize', model.dataset.variantTitle);
      } catch (e) {
        // Safari private mode — silently fail
      }

      this.setStep(this.modelStepEl, model.dataset.model, true, true);
      this.updateVariantPrice(model.dataset.variantPrice, model.dataset.variantCompareAtPrice);
      this.currentPriceCents = Number(model.dataset.variantPrice) || this.currentPriceCents;
      this.renderDeliveryEstimate();
      this.setAtcEnabled(true);
      this.addToCartButton?.focus();
    }

    setStep(stepLabelEl, content, markComplete, markActive) {
      if (!stepLabelEl) return;
      if (content) stepLabelEl.innerText = content;
      stepLabelEl.parentNode.classList.toggle(this.classes.complete, markComplete);
      stepLabelEl.parentNode.classList.toggle(this.classes.active, markActive);
    }

    setAtcEnabled(enabled) {
      if (!this.addToCartButton) return;
      this.addToCartButton.classList.toggle(this.classes.subduedBtn, !enabled);
      if (enabled) {
        this.addToCartButton.removeAttribute('disabled');
        const label = this.addToCartButton.querySelector('div');
        if (label && this.dataset.textAtc) label.innerText = this.dataset.textAtc;
      } else {
        this.addToCartButton.setAttribute('disabled', 'disabled');
      }
    }

    updateVariantPrice(variantPrice, variantCompareAtPrice) {
      this.toggleFromElement(false);

      const currencyFormat = window.themeVariables.settings.currencyCodeEnabled
        ? window.themeVariables.settings.moneyWithCurrencyFormat
        : window.themeVariables.settings.moneyFormat;

      const priceElement = this.querySelector(this.selectors.salePrice);
      const compareAtPriceElement = this.querySelector(this.selectors.compareAtPrice);

      if (priceElement) {
        const formattedPrice = formatMoney(variantPrice, currencyFormat);
        if (priceElement.textContent.trim() !== formattedPrice) {
          priceElement.lastChild.replaceWith(document.createRange().createContextualFragment(formattedPrice));
        }
        priceElement.classList.toggle(this.classes.saleText, Number(variantCompareAtPrice) > Number(variantPrice));
      }

      if (compareAtPriceElement) {
        const formattedCompareAtPrice = formatMoney(variantCompareAtPrice, currencyFormat);
        if (compareAtPriceElement.textContent.trim() !== formattedCompareAtPrice) {
          compareAtPriceElement.lastChild.replaceWith(document.createRange().createContextualFragment(formattedCompareAtPrice));
        }
        compareAtPriceElement.hidden = !(Number(variantCompareAtPrice) > Number(variantPrice));
        compareAtPriceElement.classList.add(this.classes.largeText);
      }
    }

    toggleFromElement(show) {
      const fromElement = this.querySelector(this.selectors.fromElement);
      const totalElement = this.querySelector(this.selectors.totalElement);
      if (fromElement) {
        fromElement.classList.toggle(this.classes.hidden, !show);
        totalElement?.classList.toggle(this.classes.hidden, show);
      }
    }
  }

  window.customElements.define('variant-selector-inline', VariantSelectorInline);
}
